import { DataOperationResult, getErrorMessage, useApiClient } from '@siclop/kstone-vue'

/** Résultat d'API au format `DataOperationResult` (camelCase ou PascalCase). */
function isOperationResult(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false
  return 'success' in value || 'Success' in value
}

/**
 * Vérifie le résultat d'une action métier de l'API et lève une erreur s'il n'est pas un succès.
 *
 * Le client d'API de kstone-vue ne lève pas d'exception sur `post` / `put` / `delete` : il renvoie
 * `{ success: false, errors }`. Une action dont le résultat est ignoré afficherait donc « enregistré »
 * sur un échec. Les réponses brutes (modèle renvoyé par `Ok(model)`, via `postRaw`) passent telles quelles.
 *
 * @returns la valeur utile : `result` d'un `DataOperationResult`, sinon la réponse elle-même.
 */
export function ensureSuccess<T = unknown>(response: unknown, defaultMessage: string): T {
  if (response === undefined || response === null) {
    throw new Error(defaultMessage)
  }
  if (!isOperationResult(response)) return response as T

  const success = (response.success ?? response.Success) as boolean | undefined
  if (!success) {
    const errors = (response.errors ?? response.Errors) as string[] | undefined
    const message = errors?.filter(Boolean).join(', ')
    throw new Error(message || defaultMessage)
  }
  return (response.result ?? response.Result) as T
}

/**
 * Suppression sur une route qui répond `204 No Content` (`return NoContent()`).
 *
 * `useApiClient().delete()` lit le corps de la réponse comme un `DataOperationResult` : un corps vide
 * donne `success: false` alors que la suppression a réussi. Ici, tout statut 2xx est un succès et une
 * erreur HTTP devient un échec avec le message de l'API.
 */
export async function deleteNoContent(url: string, defaultMessage: string): Promise<DataOperationResult<boolean>> {
  try {
    const response = await useApiClient().getAxiosInstance().delete(url)
    // Une route qui renvoie quand même un DataOperationResult garde son verdict.
    if (isOperationResult(response.data)) return new DataOperationResult<boolean>(response.data)
    return DataOperationResult.ok(true)
  } catch (e: unknown) {
    return DataOperationResult.fail<boolean>([getErrorMessage(e, defaultMessage)])
  }
}
